/** @jsx jsx */
import { jsx, useColorMode, useThemeUI } from 'theme-ui'
import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { nullableString } from '@chronogrove/ui/prop-types-helpers'
import { createPortal } from 'react-dom'
import { useAudioPlayerStore } from '../stores/audio-player-store'
import SoundCloud from '../shortcodes/soundcloud'
import Spotify from '../shortcodes/spotify'

const AudioPlayer = ({ soundcloudId, spotifyURL, isVisible, provider, colorMode: colorModeProp }) => {
  const containerRef = useRef(null)
  const widgetRef = useRef(null)
  const hidePlayer = useAudioPlayerStore(state => state.hidePlayer)
  // Use prop if provided (more reliable for portal re-renders), fallback to hook
  const [colorModeFromHook] = useColorMode()
  const { theme } = useThemeUI()
  const colorMode = colorModeProp || colorModeFromHook
  const isDark = colorMode === 'dark'

  // Compute actual color values from theme based on color mode
  // This ensures portal content gets correct colors regardless of CSS variable availability
  const panelBackground = isDark
    ? theme.colors?.modes?.dark?.['panel-background'] || 'rgba(20, 20, 31, 0.45)'
    : theme.colors?.['panel-background'] || 'rgba(255, 255, 255, 0.45)'
  const textColor = isDark ? theme.colors?.modes?.dark?.text || '#fff' : theme.colors?.text || '#111'

  // Create portal container on mount
  useEffect(() => {
    if (!containerRef.current) {
      containerRef.current = document.createElement('div')
      containerRef.current.id = 'audio-player-portal'
      document.body.appendChild(containerRef.current)
    }

    return () => {
      if (containerRef.current) {
        document.body.removeChild(containerRef.current)
        containerRef.current = null
      }
    }
  }, [])

  // Update widget when soundcloudId changes
  useEffect(() => {
    if (soundcloudId) {
      widgetRef.current = soundcloudId
    }
  }, [soundcloudId])

  // Stable key per track so React preserves the same embed instance across re-renders
  // (e.g. on page navigation). Without this, Spotify remounts and playback stops.
  const renderEmbed = () => {
    if (provider === 'soundcloud' && soundcloudId) {
      return <SoundCloud key={soundcloudId} soundcloudId={soundcloudId} />
    }
    if (provider === 'spotify' && spotifyURL) {
      return <Spotify key={spotifyURL} spotifyURL={spotifyURL} />
    }
    return null
  }

  if (!isVisible || !provider || !containerRef.current) return null

  return createPortal(
    <div
      // Key forces React to re-mount when color mode changes, ensuring fresh styles
      key={`audio-player-${colorMode}`}
      // Use inline style for color-mode-dependent values to ensure they update on toggle
      // sx prop CSS-in-JS styles can be cached and not update properly in portals
      style={{
        background: panelBackground,
        boxShadow: isDark ? '0 -2px 10px rgba(0,0,0,0.3)' : '0 -2px 10px rgba(0,0,0,0.1)'
      }}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)', // for Safari
        pt: 2,
        pb: 3,
        px: 3,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <div
        sx={{
          width: '100%',
          maxWidth: [
            '100%', // mobile: full width
            '100%', // tablet: full width
            '1200px', // desktop: max-width
            '1400px' // large desktop: slightly wider
          ],
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end'
        }}
      >
        <button
          onClick={() => hidePlayer()}
          // Use inline style for color to ensure it updates on color mode toggle
          style={{ color: textColor }}
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            transition: 'all 0.2s ease',
            mb: 1,
            '&:hover': {
              background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
            }
          }}
          aria-label='Close audio player'
        >
          <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M12 4L4 12M4 4L12 12'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </button>

        <div
          sx={{
            width: '100%',
            '& iframe': {
              border: 'none',
              width: '100% !important',
              height: '100px !important', // fixed compact height
              maxHeight: '100px !important'
            }
          }}
        >
          {renderEmbed()}
        </div>
      </div>
    </div>,
    containerRef.current
  )
}

/** `useAudioPlayerStore` uses `null` when idle (see `initialAudioPlayerState`). */
const audioProvider = PropTypes.oneOfType([PropTypes.oneOf(['soundcloud', 'spotify']), PropTypes.oneOf([null])])

AudioPlayer.propTypes = {
  soundcloudId: nullableString,
  spotifyURL: nullableString,
  isVisible: PropTypes.bool,
  provider: audioProvider,
  colorMode: nullableString
}

export default AudioPlayer
